import React from 'react';
import './App.css';
import { MarineIcon, XenosIcon, ServitorIcon } from './components/DwIcons';
import DeathwatchRoller from './components/DeathwatchRoller';
import RequisitionShop from './components/RequisitionShop';
import PlayerTab from './components/PlayerTab';
import RulesTab from './components/RulesTab';
import BestiaryTab from './components/BestiaryTab';
import WeaponsTab from './components/WeaponsTab';
import GMKit from './components/GMKit';
import PlayerManagement from './components/PlayerManagement';
import MissionTab from './components/MissionTab';
import SimulationTab from './components/SimulationTab';
import ErrorReports from './components/ErrorReports';
import BattlemapTab from './components/BattlemapTab';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { debug, info, warn, error, logApiCall, logApiError, logUserAction } from './utils/logger';

function App() {
  const [tab, setTab] = useState('mission');
  const [authedPlayer, setAuthedPlayer] = useState(() => localStorage.getItem('dw:shop:authedPlayer') ? JSON.parse(localStorage.getItem('dw:shop:authedPlayer')) : '');
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('dw:shop:sessionId') ? JSON.parse(localStorage.getItem('dw:shop:sessionId')) : '');
  const [loginName, setLoginName] = useState('');
  const [loginPw, setLoginPw] = useState('');
  const [loginMsg, setLoginMsg] = useState('');
  const [players, setPlayers] = useState([]);
  const [playersError, setPlayersError] = useState('');

  async function fetchPlayers() {
    try {
      setPlayersError('')
      const res = await axios.get('/api/players/names');
      setPlayers(res.data || []);
    } catch (err) {
      console.error('fetchPlayers failed', err);
      setPlayers([]);
      setPlayersError('Failed to load users — is the backend running? Check console/network.');
    }
  }

  useEffect(() => {
    fetchPlayers();
  }, []);

  useEffect(() => {
    function openRollerFromMission() {
      setTab('roller');
    }
    window.addEventListener('dw:open-roller', openRollerFromMission);
    return () => window.removeEventListener('dw:open-roller', openRollerFromMission);
  }, []);

  useEffect(() => {
    async function validate() {
      if (sessionId) {
        try {
          debug(`Validating session: ${sessionId}`, 'auth');
          const res = await axios.post('/api/sessions/validate', { sessionId });
          logApiCall('POST', '/api/sessions/validate', { sessionId }, res.status);
          if (res.data && res.data.playerName) {
            setAuthedPlayer(res.data.playerName);
            localStorage.setItem('dw:shop:authedPlayer', JSON.stringify(res.data.playerName));
            try {
              const fullPlayerResponse = await axios.get(`/api/players/${res.data.playerName}`, {
                headers: { 'x-session-id': sessionId }
              });
              localStorage.setItem('dw:shop:playerData', JSON.stringify(fullPlayerResponse.data));
              info(`Session validation and player data fetch successful for: ${res.data.playerName}`, 'auth');
            } catch (playerFetchError) {
              warn(`Failed to fetch full player data during session validation: ${playerFetchError.message}`, 'auth');
            }
            info(`Session validation successful for: ${res.data.playerName}`, 'auth');
          } else {
            warn('Session validation failed - invalid response', 'auth');
            setAuthedPlayer('');
            setSessionId('');
            localStorage.removeItem('dw:shop:authedPlayer');
            localStorage.removeItem('dw:shop:sessionId');
          }
        } catch (err) {
          logApiError('App', 'POST', '/api/sessions/validate', err);
          error(`Session validation error: ${err.message}`, 'auth');
          setAuthedPlayer('');
          setSessionId('');
          localStorage.removeItem('dw:shop:authedPlayer');
          localStorage.removeItem('dw:shop:sessionId');
        }
      } else {
        debug('No session ID found for validation', 'auth');
      }
    }
    validate();
  }, [sessionId]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  const handleLogin = async () => {
    try {
      logUserAction('user', 'Login attempt', { username: loginName });
      const response = await axios.post('/api/players/login', {
        name: loginName,
        password: loginPw
      });
      logApiCall('POST', '/api/players/login', { name: loginName }, response.status);
      if (response.data.sessionId && response.data.player) {
        setAuthedPlayer(response.data.player.name);
        setSessionId(response.data.sessionId);
        localStorage.setItem('dw:shop:authedPlayer', JSON.stringify(response.data.player.name));
        localStorage.setItem('dw:shop:sessionId', JSON.stringify(response.data.sessionId));
        try {
          const fullPlayerResponse = await axios.get(`/api/players/${response.data.player.name}`, {
            headers: { 'x-session-id': response.data.sessionId }
          });
          localStorage.setItem('dw:shop:playerData', JSON.stringify(fullPlayerResponse.data));
          info(`Full player data loaded for: ${loginName}`, 'auth');
        } catch (playerFetchError) {
          warn(`Failed to fetch full player data: ${playerFetchError.message}`, 'auth');
          localStorage.setItem('dw:shop:playerData', JSON.stringify(response.data.player));
        }
        info(`Login successful for user: ${loginName}`, 'auth');
        logUserAction('user', 'Login successful', { username: loginName });
        setLoginMsg('Login successful!');
        setLoginName('');
        setLoginPw('');
        setTimeout(() => setLoginMsg(''), 3000);
      } else {
        warn(`Login failed for user: ${loginName} - ${response.data.message || 'Unknown error'}`, 'auth');
        setLoginMsg(response.data.message || 'Login failed');
        setTimeout(() => setLoginMsg(''), 5000);
      }
    } catch (err) {
      logApiError('App', 'POST', '/api/players/login', err);
      error(`Login error for user: ${loginName} - ${err.message}`, 'auth');
      setLoginMsg('Login failed. Please check your credentials and try again.');
      setTimeout(() => setLoginMsg(''), 5000);
    }
  };

  async function handleLogout() {
    logUserAction('user', 'Logout attempt', { username: authedPlayer });
    if (sessionId) {
      try {
        await axios.post('/api/sessions/logout', { sessionId });
        info(`Logout successful for user: ${authedPlayer}`, 'auth');
      } catch (err) {
        warn(`Logout API call failed: ${err.message}`, 'auth');
      }
    }
    setAuthedPlayer('');
    setSessionId('');
    localStorage.removeItem('dw:shop:authedPlayer');
    localStorage.removeItem('dw:shop:sessionId');
    setLoginMsg('Logged out successfully');
    info('User logged out successfully', 'auth');
    setTimeout(() => setLoginMsg(''), 3000);
  }

  const [appBackgroundUrl, setAppBackgroundUrl] = useState(() => {
    try {
      return localStorage.getItem('dw:gmkit:background') || '/gmkit/deathwatch-banner.png';
    } catch (e) {
      return '/gmkit/deathwatch-banner.png';
    }
  });

  useEffect(() => {
    let mounted = true;
    async function pickBackground() {
      try {
        const res = await axios.get('/api/gmkit/list');
        const list = Array.isArray(res.data) ? res.data : [];
        if (!mounted) return;
        const banner = list.find(f => f.name === 'deathwatch-banner.png');
        if (banner) return setAppBackgroundUrl(banner.url);
        const firstImg = list.find(f => ['png', 'jpg', 'jpeg'].includes(f.ext));
        if (firstImg) return setAppBackgroundUrl(firstImg.url);
      } catch (err) {
        console.debug('Could not load gmkit list for background', err.message || err);
      }
    }
    pickBackground();
    return () => { mounted = false; }
  }, []);

  const appBackgroundStyle = {
    backgroundImage: `linear-gradient(rgba(10,12,16,0.85), rgba(10,12,16,0.85)), url('${appBackgroundUrl}')`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'left center, right center',
    backgroundSize: '25%, 25%',
    backgroundAttachment: 'fixed',
    backgroundBlendMode: 'multiply'
  };

  const navButtonClass = (active, accent = 'red') => {
    return `dw-nav-btn ${active ? 'dw-nav-btn--active' : ''} ${accent === 'gold' && active ? 'dw-nav-btn--gm' : ''}`;
  };

  const tabs = [
    { id: 'mission', label: 'Mission', icon: <MarineIcon size={16} /> },
    { id: 'roller', label: 'Dice Roller', icon: <MarineIcon size={16} /> },
    { id: 'shop', label: 'Requisition', icon: <MarineIcon size={16} /> },
    { id: 'player', label: 'Character', icon: <MarineIcon size={16} /> },
    { id: 'rules', label: 'Rules', icon: <MarineIcon size={16} /> },
    { id: 'weapons', label: 'Weapons', icon: <MarineIcon size={16} /> },
    { id: 'battlemap', label: 'Battlemap', icon: <MarineIcon size={16} /> },
  ];

  const gmTabs = [
    { id: 'bestiary', label: 'Bestiary', icon: <XenosIcon size={16} /> },
    { id: 'players', label: 'Players', icon: <ServitorIcon size={16} /> },
    { id: 'gmkit', label: 'GM Kit', icon: <MarineIcon size={16} /> },
    { id: 'simulation', label: 'Sim Lab', icon: <ServitorIcon size={16} /> },
  ];

  return (
    <div className="dw-app min-h-screen" style={tab === 'players' ? appBackgroundStyle : {}}>
      {/* ── Deathwatch Header ── */}
      <header className="dw-header">
        <div className="dw-header-inner">
          {/* Title + Login Row */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <h1 className="dw-title">
              <img src="/40k-logo.svg" alt="" className="dw-title-logo" />
              <span className="dw-title-accent">Death</span>watch Roller
            </h1>

            <div className="flex-shrink-0 min-w-0">
              {authedPlayer ? (
                <div className="flex items-center gap-3">
                  <span className="dw-status dw-status--online">
                    {authedPlayer}
                  </span>
                  <button onClick={handleLogout} className="dw-btn" style={{ borderColor: 'var(--dw-red-dim)', color: 'var(--dw-red-bright)' }}>
                    Logout
                  </button>
                  {loginMsg && <span className="dw-badge dw-badge--green">{loginMsg}</span>}
                </div>
              ) : (
                <div className="flex gap-2 items-center">
                  <input
                    className="dw-input"
                    style={{ width: '100px' }}
                    type="text"
                    placeholder="Callsign"
                    value={loginName}
                    onChange={e => { setLoginName(e.target.value); setLoginPw(e.target.value === 'gm' ? '' : '1234'); }}
                    data-testid="login-user-input"
                  />
                  <input
                    className="dw-input"
                    style={{ width: '110px' }}
                    type="password"
                    placeholder="Password"
                    value={loginPw}
                    onChange={e => setLoginPw(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                  <button onClick={handleLogin} className="dw-btn dw-btn--primary">
                    Login
                  </button>
                  {loginMsg && <span className="dw-badge dw-badge--red">{loginMsg}</span>}
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="dw-nav">
            {tabs.map(t => (
              <button
                key={t.id}
                className={navButtonClass(tab === t.id)}
                onClick={() => { logUserAction('navigation', 'Tab switch', { from: tab, to: t.id }); setTab(t.id); }}
              >
                <span className="dw-nav-icon">{t.icon}</span>
                {t.label}
              </button>
            ))}
            <a
              href="/cards.html"
              target="_blank"
              rel="noopener noreferrer"
              className="dw-nav-btn"
            >
              Cards
            </a>
            <a
              href="/print.html"
              target="_blank"
              rel="noopener noreferrer"
              className="dw-nav-btn"
            >
              Print
            </a>
            <button
              className={navButtonClass(tab === 'errors', 'gold')}
              onClick={() => { logUserAction('navigation', 'Tab switch', { from: tab, to: 'errors' }); setTab('errors'); }}
            >
              Errors
            </button>
            {authedPlayer === 'gm' && gmTabs.map(t => (
              <button
                key={t.id}
                className={navButtonClass(tab === t.id, 'gold')}
                onClick={() => { logUserAction('navigation', 'Tab switch', { from: tab, to: t.id }); setTab(t.id); }}
              >
                <span className="dw-nav-icon">{t.icon}</span>
                {t.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <div className="mx-auto max-w-6xl px-2 py-3 sm:px-6 sm:py-6" style={{ maxWidth: 'var(--dw-container-max)' }}>
        {!authedPlayer && (
          <div className="dw-card dw-card--red mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="dw-h4" style={{ marginTop: 0 }}>Available Operatives</h3>
              <button onClick={fetchPlayers} className="dw-btn" style={{ fontSize: '9px' }}>Refresh</button>
            </div>
            {playersError && <div className="dw-text dw-text--small" style={{ color: 'var(--dw-red-bright)' }}>{playersError}</div>}
            <div className="flex flex-wrap gap-2">
              {players && players.length > 0 ? players.map(player => (
                <button
                  key={player.name}
                  onClick={() => { setLoginName(player.name); setLoginPw(player.name === 'gm' ? '' : '1234'); }}
                  className="dw-tag"
                >
                  {player.name}
                </button>
              )) : (
                <span className="dw-text dw-text--small dw-text--muted">No operatives found.</span>
              )}
            </div>
            <p className="dw-text dw-text--small dw-text--muted" style={{ marginTop: '0.5rem' }}>
              Marine passwords auto-fill. GM password required.
            </p>
          </div>
        )}

        {tab === 'roller' ? <DeathwatchRoller authedPlayer={authedPlayer} /> :
         tab === 'shop' ? <RequisitionShop authedPlayer={authedPlayer} sessionId={sessionId} /> :
         tab === 'rules' ? <RulesTab authedPlayer={authedPlayer} sessionId={sessionId} /> :
         tab === 'weapons' ? <WeaponsTab /> :
         tab === 'battlemap' ? <BattlemapTab authedPlayer={authedPlayer} sessionId={sessionId} /> :
         tab === 'bestiary' ? (authedPlayer === 'gm' ? <BestiaryTab sessionId={sessionId} /> :
           <div className="dw-card dw-card--red"><h2 className="dw-h3" style={{ color: 'var(--dw-red-bright)', marginTop: 0 }}>Access Denied</h2><p className="dw-text">The Bestiary is restricted to Chapter Masters.</p></div>) :
         tab === 'players' ? <PlayerManagement authedPlayer={authedPlayer} sessionId={sessionId} /> :
         tab === 'gmkit' ? <GMKit authedPlayer={authedPlayer} /> :
         tab === 'simulation' ? <SimulationTab authedPlayer={authedPlayer} sessionId={sessionId} /> :
         tab === 'errors' ? <ErrorReports authedPlayer={authedPlayer} sessionId={sessionId} /> :
         tab === 'mission' ? <MissionTab authedPlayer={authedPlayer} sessionId={sessionId} /> :
         <PlayerTab authedPlayer={authedPlayer} sessionId={sessionId} />}
      </div>
    </div>
  );
}

export default App;